import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { ListWrapper } from 'components/AgGrid/CellRenderer/ListView.styled';
import { NumericCellRenderer } from 'components/AgGrid/CellEditRenderer/NumericCellEditRenderer';
import { TextCellEditRenderer } from 'components/AgGrid/CellEditRenderer/TextCellEditRenderer';
import * as React from 'react';
import { type MakeModelDetails } from 'types/makeModel.types';
import { useAppSelector } from 'store/hook';
import {
  useMakeModelService,
  isValidNumber,
  validateMakeModelData,
} from 'services/makeModelData.service';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { Snackbar, Alert, Box, IconButton, Tooltip } from '@mui/material';
import { EditOutlined, Save, Cancel } from '@mui/icons-material';
import { MakeModelAPIError } from 'api/makeModel.api';
import useAppTheme from 'hooks/useAppTheme';
import ConfirmationModal from 'views/Settings/ExternalUsers/Squares/WorkOrderManagement/components/ConfirmationModal';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { isExternalUser } from 'utils/validators/role.validation';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

/**
 * Snackbar state interface
 */
interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

/**
 * Cell value change parameters from ag-grid
 */
interface CellValueChangeParams {
  data: MakeModelDetails;
  colDef: ColDef;
  newValue: string | number | null;
  oldValue: string | number | null;
  node: {
    setDataValue: (field: string, value: string | number | null) => void;
  };
}

/**
 * A component that displays a list of make model details
 * Allows editing of vehicle configuration parameters
 */
export const MakeModelList = () => {
  const { t } = useTranslation();
  const { customer, role } = useAppSelector((state) => state.authReducer);
  const currentCustomerId = customer.id;
  const isDisabled = isExternalUser(role);
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  // Use the service hook
  const { getMakeModelDetails, updateMakeModelDetailsBulk } =
    useMakeModelService();

  // Component state
  const [makeModelDetails, setMakeModelDetails] = React.useState<
    MakeModelDetails[]
  >([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [savingRowId, setSavingRowId] = React.useState<string | null>(null);
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [originalData, setOriginalData] =
    React.useState<MakeModelDetails | null>(null);
  const [gridApi, setGridApi] = React.useState<any>(null);
  const [confirmationModal, setConfirmationModal] = React.useState<{
    open: boolean;
    rowData: MakeModelDetails | null;
  }>({
    open: false,
    rowData: null,
  });

  /**
   * Show snackbar notification
   */
  const showSnackbar = React.useCallback(
    (message: string, severity: 'success' | 'error') => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  /**
   * Close snackbar notification
   */
  const handleCloseSnackbar = React.useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  /**
   * Extract error message from various error types
   */
  const getErrorMessage = React.useCallback((error: unknown): string => {
    if (error instanceof MakeModelAPIError) {
      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'An unexpected error occurred';
  }, []);

  /**
   * Handle grid ready event
   */
  const handleGridReady = React.useCallback((params: any) => {
    setGridApi(params.api);
  }, []);

  /**
   * Handle edit mode toggle for a row
   */
  const handleEditClick = React.useCallback(
    (rowData: MakeModelDetails) => {
      setEditingRowId(rowData.id);
      setOriginalData({ ...rowData });

      // Refresh the grid to update editable states
      if (gridApi) {
        setTimeout(() => {
          gridApi.refreshCells({ force: true });
        }, 100);
      }
    },
    [gridApi]
  );

  /**
   * Handle save changes for a row - single API call with validation
   */
  const handleSaveClick = React.useCallback(
    async (rowData: MakeModelDetails) => {
      // Check if current capacity is greater than max payload capacity
      console.log('Checking capacity validation:', {
        current_capacity: rowData.current_capacity,
        max_payload_capacity: rowData.max_payload_capacity,
        currentType: typeof rowData.current_capacity,
        maxType: typeof rowData.max_payload_capacity,
        comparison:
          Number(rowData.current_capacity) >
          Number(rowData.max_payload_capacity),
      });

      if (
        rowData.current_capacity !== null &&
        rowData.current_capacity !== undefined &&
        rowData.max_payload_capacity !== null &&
        rowData.max_payload_capacity !== undefined &&
        Number(rowData.current_capacity) > Number(rowData.max_payload_capacity)
      ) {
        console.log('Showing confirmation modal');
        // Show confirmation modal instead of blocking
        setConfirmationModal({
          open: true,
          rowData: rowData,
        });
        return;
      }

      // Validate the data using service validation (excluding capacity check)
      const validationResult = validateMakeModelData(rowData);
      if (!validationResult.isValid) {
        const firstError = validationResult.errors[0];
        showSnackbar(firstError.message, 'error');
        return;
      }

      setSavingRowId(rowData.id);

      try {
        // Prepare payload with all changed fields
        const editableFields = [
          'idle_rpm',
          'fuel_source',
          'max_payload_capacity',
          'current_capacity',
        ];

        const updatePayload: any = {};
        let hasChanges = false;

        // Build payload with only changed fields
        editableFields.forEach((field) => {
          const currentValue = rowData[field as keyof MakeModelDetails];
          const originalValue = originalData?.[field as keyof MakeModelDetails];

          if (currentValue !== originalValue) {
            updatePayload[field] = currentValue;
            hasChanges = true;
          }
        });

        // Only make API call if there are changes
        if (hasChanges) {
          // Use the service bulk update function
          await updateMakeModelDetailsBulk(rowData.id, updatePayload);
        }

        showSnackbar('Make model updated successfully', 'success');
        setEditingRowId(null);
        setOriginalData(null);

        // Refresh the grid to update editable states
        if (gridApi) {
          setTimeout(() => {
            gridApi.refreshCells({ force: true });
          }, 100);
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        showSnackbar(errorMessage, 'error');

        // Revert changes on error
        if (originalData) {
          setMakeModelDetails((prev) =>
            prev.map((item) => (item.id === rowData.id ? originalData : item))
          );
        }
      } finally {
        setSavingRowId(null);
      }
    },
    [
      showSnackbar,
      getErrorMessage,
      originalData,
      gridApi,
      updateMakeModelDetailsBulk,
    ]
  );

  /**
   * Handle confirmation modal close
   */
  const handleConfirmationClose = React.useCallback(() => {
    setConfirmationModal({
      open: false,
      rowData: null,
    });
  }, []);

  /**
   * Handle confirmation modal confirm - proceed with API call
   */
  const handleConfirmationConfirm = React.useCallback(async () => {
    if (!confirmationModal.rowData) {
      return;
    }

    const rowData = confirmationModal.rowData;
    setConfirmationModal({
      open: false,
      rowData: null,
    });

    // Validate the data using service validation (excluding capacity check)
    const validationResult = validateMakeModelData(rowData);
    if (!validationResult.isValid) {
      const firstError = validationResult.errors[0];
      showSnackbar(firstError.message, 'error');
      return;
    }

    setSavingRowId(rowData.id);

    try {
      // Prepare payload with all changed fields
      const editableFields = [
        'idle_rpm',
        'fuel_source',
        'max_payload_capacity',
        'current_capacity',
      ];

      const updatePayload: any = {};
      let hasChanges = false;

      // Build payload with only changed fields
      editableFields.forEach((field) => {
        const currentValue = rowData[field as keyof MakeModelDetails];
        const originalValue = originalData?.[field as keyof MakeModelDetails];

        if (currentValue !== originalValue) {
          updatePayload[field] = currentValue;
          hasChanges = true;
        }
      });

      // Only make API call if there are changes
      if (hasChanges) {
        // Use the service bulk update function
        await updateMakeModelDetailsBulk(rowData.id, updatePayload);
      }

      showSnackbar('Make model updated successfully', 'success');
      setEditingRowId(null);
      setOriginalData(null);

      // Refresh the grid to update editable states
      if (gridApi) {
        setTimeout(() => {
          gridApi.refreshCells({ force: true });
        }, 100);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      showSnackbar(errorMessage, 'error');

      // Revert changes on error
      if (originalData) {
        setMakeModelDetails((prev) =>
          prev.map((item) => (item.id === rowData.id ? originalData : item))
        );
      }
    } finally {
      setSavingRowId(null);
    }
  }, [
    confirmationModal.rowData,
    showSnackbar,
    getErrorMessage,
    originalData,
    gridApi,
    updateMakeModelDetailsBulk,
  ]);

  /**
   * Handle cancel edit for a row
   */
  const handleCancelClick = React.useCallback(
    (rowData: MakeModelDetails) => {
      if (originalData) {
        // Revert changes by updating the row data
        setMakeModelDetails((prev) =>
          prev.map((item) => (item.id === rowData.id ? originalData : item))
        );
      }
      setEditingRowId(null);
      setOriginalData(null);

      // Refresh the grid to update editable states
      if (gridApi) {
        setTimeout(() => {
          gridApi.refreshCells({ force: true });
        }, 100);
      }
    },
    [originalData, gridApi]
  );

  /**
   * Handle cell value changes in the grid (only when in edit mode)
   */
  const handleCellValueChanged = React.useCallback(
    (params: CellValueChangeParams) => {
      const { data, colDef, newValue } = params;

      // Only allow editing when row is in edit mode
      if (editingRowId !== data.id) {
        return;
      }

      // Validate numeric fields using service validation
      if (
        colDef.field === 'current_capacity' ||
        colDef.field === 'max_payload_capacity'
      ) {
        if (!isValidNumber(newValue)) {
          const fieldName =
            colDef.field === 'current_capacity'
              ? 'Current capacity'
              : 'Max payload capacity';
          showSnackbar(`${fieldName} must be a valid number`, 'error');
          return;
        }
      }

      // Update local state
      setMakeModelDetails((prev) =>
        prev.map((item) =>
          item.id === data.id
            ? { ...item, [colDef.field as string]: newValue }
            : item
        )
      );
    },
    [editingRowId, showSnackbar]
  );

  /**
   * Fetch make model details from the API
   */
  const fetchMakeModelDetails = React.useCallback(async () => {
    if (!currentCustomerId) {
      setError('Missing customer ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getMakeModelDetails(currentCustomerId);
      setMakeModelDetails(response);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentCustomerId]);

  /**
   * Retry fetching data
   */
  const handleRetry = React.useCallback(() => {
    void fetchMakeModelDetails();
  }, [fetchMakeModelDetails]);

  // Fetch data on component mount and when dependencies change
  React.useEffect(() => {
    void fetchMakeModelDetails();
  }, [fetchMakeModelDetails]);

  /**
   * Column definitions for the ag-grid
   */
  const columnDefs = React.useMemo<ColDef[]>(
    () => [
      {
        field: 'make',
        headerName: 'Make',
        minWidth: 120,
        filter: 'agTextColumnFilter',
        sortable: true,
        editable: false, // Make and Model are not editable
      },
      {
        field: 'model',
        headerName: t('model_10'),
        minWidth: 120,
        filter: 'agTextColumnFilter',
        sortable: true,
        editable: false, // Make and Model are not editable
      },
      {
        field: 'assetType',
        headerName: t('asset_type'),
        minWidth: 150,
        filter: 'agTextColumnFilter',
        sortable: true,
        editable: false, // Asset Type is not editable
      },
      {
        field: 'idle_rpm',
        headerName: t('idle_rpm'),
        minWidth: 100,
        filter: 'agNumberColumnFilter',
        sortable: true,
        editable: (params: any) =>
          !isDisabled && editingRowId === params.data.id,
        cellEditor: NumericCellRenderer,
        cellRenderer: (params: ICellRendererParams) => params.value ?? '--',
        cellStyle: (params: any) => {
          const isEditable = editingRowId === params.data.id;
          return {
            backgroundColor: isEditable
              ? enableDarkTheme
                ? palette.boxBackground
                : `${palette.primary}0D`
              : 'transparent',
            cursor: isEditable ? 'pointer' : 'default',
            border: isEditable ? `1px solid ${palette.primary}` : 'none',
            borderRadius: isEditable ? '4px' : '0',
          };
        },
      },
      {
        field: 'fuel_source',
        headerName: t('fuel_source_2'),
        minWidth: 120,
        filter: 'agTextColumnFilter',
        sortable: true,
        editable: (params: any) =>
          !isDisabled && editingRowId === params.data.id,
        cellEditor: TextCellEditRenderer,
        cellRenderer: (params: ICellRendererParams) => params.value ?? '--',
        cellStyle: (params: any) => {
          const isEditable = editingRowId === params.data.id;
          return {
            backgroundColor: isEditable
              ? enableDarkTheme
                ? palette.boxBackground
                : `${palette.primary}0D`
              : 'transparent',
            cursor: isEditable ? 'pointer' : 'default',
            border: isEditable ? `1px solid ${palette.primary}` : 'none',
            borderRadius: isEditable ? '4px' : '0',
          };
        },
      },
      {
        field: 'max_payload_capacity',
        headerName: t('max_payload_capacity_ton'),
        minWidth: 250,
        filter: 'agNumberColumnFilter',
        sortable: true,
        editable: false,
      },
      {
        field: 'current_capacity',
        headerName: t('current_capacity_ton'),
        minWidth: 250,
        filter: 'agNumberColumnFilter',
        sortable: true,
        editable: (params: any) =>
          !isDisabled && editingRowId === params.data.id,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          precision: 2, // Allow 2 decimal places
          step: 0.01, // Step by 0.01
        },
        cellValidator: (params: any) => {
          const value = params.value;
          if (value === null || value === undefined || value === '') {
            return true; // Allow empty values
          }

          const numValue = Number(value);
          if (isNaN(numValue) || !isFinite(numValue)) {
            return {
              isValid: false,
              message: t('current_capacity_must_be_a_valid_number'),
            };
          }

          return true;
        },
        cellRenderer: (params: ICellRendererParams) => {
          return params.value ? Number(params.value).toFixed(2) : '--';
        },
        cellStyle: (params: any) => {
          const isEditable = editingRowId === params.data.id;
          return {
            backgroundColor: isEditable
              ? enableDarkTheme
                ? palette.boxBackground
                : `${palette.primary}0D`
              : 'transparent',
            cursor: isEditable ? 'pointer' : 'default',
            border: isEditable ? `1px solid ${palette.primary}` : 'none',
            borderRadius: isEditable ? '4px' : '0',
          };
        },
      },
      {
        field: 'actions',
        headerName: t('actions'),
        width: 120,
        pinned: 'right',
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<MakeModelDetails>) => {
          const isEditing = editingRowId === params.data?.id;
          const isSaving = savingRowId === params.data?.id;

          return (
            <Box display="flex" alignItems="center" gap={1}>
              {isEditing ? (
                <>
                  <Tooltip arrow title={t('save')}>
                    <IconButton
                      size="small"
                      disabled={isSaving}
                      onClick={() => {
                        if (params.data) {
                          void handleSaveClick(params.data);
                        }
                      }}
                      sx={{
                        border: '1px solid #4caf50',
                        borderRadius: '8px',
                        color: '#4caf50',
                        '&:hover': {
                          backgroundColor: enableDarkTheme
                            ? '#4caf5026'
                            : '#4caf501a',
                        },
                      }}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow title={t('cancel')}>
                    <IconButton
                      size="small"
                      disabled={isSaving}
                      onClick={() => {
                        if (params.data) {
                          handleCancelClick(params.data);
                        }
                      }}
                      sx={{
                        border: '1px solid #f44336',
                        borderRadius: '8px',
                        color: '#f44336',
                        '&:hover': {
                          backgroundColor: enableDarkTheme
                            ? '#f4433626'
                            : '#f443361a',
                        },
                      }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip arrow title={t('edit')}>
                  <IconButton
                    size="small"
                    disabled={isSaving}
                    onClick={() => {
                      if (params.data) {
                        handleEditClick(params.data);
                      }
                    }}
                    sx={{
                      border: `1px solid ${palette.primary}`,
                      borderRadius: '8px',
                      color: palette.primary,
                      '&:hover': {
                        backgroundColor: enableDarkTheme
                          ? `${palette.primary}26`
                          : `${palette.primary}1A`,
                      },
                    }}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
        hide: isDisabled,
      },
    ],
    [
      editingRowId,
      savingRowId,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      enableDarkTheme,
    ]
  );

  /**
   * Error display component
   */
  const ErrorDisplay = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <p style={{ color: palette.danger }}>{error}</p>
      <button onClick={handleRetry}>Retry</button>
    </div>
  );

  // Loading state
  if (loading) {
    return <SingleFadeLoader />;
  }

  // Error state
  if (error) {
    return <ErrorDisplay />;
  }

  // Main content
  return (
    <>
      <ListWrapper>
        <BaseTableList<MakeModelDetails>
          rowData={makeModelDetails}
          columnDefs={columnDefs}
          onCellValueChanged={handleCellValueChanged}
          onGridReady={handleGridReady}
          singleClickEdit={true}
        />
      </ListWrapper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {confirmationModal?.open && (
        <CustomMuiThemeProvider>
          <ConfirmationModal
            open={confirmationModal.open}
            onClose={handleConfirmationClose}
            onConfirm={handleConfirmationConfirm}
            title={t('capacity_validation_warning')}
            message={`The current capacity (${confirmationModal.rowData?.current_capacity} tons) is greater than the max payload capacity (${confirmationModal.rowData?.max_payload_capacity} tons). Are you sure you want to proceed with saving this data?`}
            confirmButtonColor="primary"
          />
        </CustomMuiThemeProvider>
      )}
    </>
  );
};
